
import { elem, styleInstaller } from "./chrome";

export interface StatusResetActions {
  /** Advance the server-issued reset dialogue.  It has no client state. */
  onAccept?: () => void;
}

export type StatusResetStage = "offer" | "confirm";

const STYLE_ID = "status-reset-style";

const STYLE = `
.status-reset {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  z-index: 75; width: min(400px, calc(100vw - 32px)); padding: 16px;
  border: 1px solid rgba(255, 214, 142, .38); border-radius: 10px;
  background: rgba(10, 12, 16, .96); box-shadow: 0 14px 44px rgba(0, 0, 0, .65);
  color: #e6e0cf; font: 13px/1.5 system-ui, sans-serif; pointer-events: auto;
}
.status-reset-title { color: #ffe6ba; font-weight: 650; margin-bottom: 9px; }
.status-reset-copy { white-space: pre-line; color: #cbd2da; }
.status-reset-actions { display: flex; justify-content: end; gap: 8px; margin-top: 14px; }
.status-reset button {
  padding: 6px 12px; border: 1px solid rgba(255, 236, 170, .3); border-radius: 6px;
  background: rgba(255, 236, 170, .08); color: #ffe6ba; font: inherit; cursor: pointer;
}
.status-reset button:hover { background: rgba(255, 236, 170, .18); }
`;

const COPY: Record<StatusResetStage, Readonly<{ title: string; body: string }>> = {
  // `gameforge.reset_scroll._10_sayTitle` / `._20_say`.
  offer: {
    title: "Reset status",
    body:
      "This scroll allows you to reset all your previous status improvements. Your whole status will be affected: VIT, INT, STR and DEX will all be reset to 1. The points you gain from this can be redistributed.\n\nDo you want to use the scroll now?",
  },
  // `gameforge.reset_scroll._30_sayTitle` / `._40_say`.
  confirm: {
    title: "Status reset: Confirm",
    body: "All your status points will be reset to 1.\nDo you really want to reset your status?",
  },
};

const installStyle = styleInstaller(STYLE_ID, STYLE);

/** A server-driven reset confirmation.  Closing it never sends a cancellation. */
export class StatusResetPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly body: HTMLElement;
  private readonly accept: HTMLButtonElement;
  private stage: StatusResetStage | null = null;

  constructor(container: HTMLElement, private readonly actions: StatusResetActions = {}) {
    const doc = container.ownerDocument ?? document;
    installStyle(doc);
    this.root = elem(doc, "section", "status-reset");
    this.root.hidden = true;
    this.root.tabIndex = -1;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    // PointResetDialog.OnPressEscapeKey closes both legacy windows without
    // answering the reset request (`uipointreset.py:67-74`).
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });

    this.title = elem(doc, "div", "status-reset-title");
    this.body = elem(doc, "div", "status-reset-copy");
    const actionRow = elem(doc, "div", "status-reset-actions");
    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.close());
    this.accept = doc.createElement("button");
    this.accept.type = "button";
    this.accept.textContent = "Continue";
    this.accept.addEventListener("click", () => this.advance());
    actionRow.append(cancel, this.accept);
    this.root.append(this.title, this.body, actionRow);
    container.append(this.root);
  }

  /** Draw exactly the stage the server issued, replacing any stale question. */
  open(stage: StatusResetStage): void {
    this.stage = stage;
    const copy = COPY[stage];
    this.title.textContent = copy.title;
    this.body.textContent = copy.body;
    this.root.hidden = false;
    this.root.focus();
    this.accept.focus();
  }

  /** First legacy question, sent after `UseItem` found a live reset scroll. */
  offer(): void {
    this.open("offer");
  }

  /** Second legacy question, sent only after the first fieldless acceptance. */
  confirm(): void {
    this.open("confirm");
  }

  private advance(): void {
    // A double-click on a detached button must not turn one displayed question
    // into two server replies.  The next stage is server-issued; no local code
    // invents or skips it.
    if (this.stage === null) return;
    this.close();
    this.actions.onAccept?.();
  }

  close(): void {
    this.stage = null;
    this.root.hidden = true;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
